import React, { createContext, useState, useEffect, useCallback } from "react";
import axios from "axios";
import config from "../config";

export const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(null);
  const [favourites, setFavourites] = useState([]);
  const [loading, setLoading] = useState(true);

  // Load initial state from localStorage
  useEffect(() => {
    const storedUser = localStorage.getItem("user");
    const storedToken = localStorage.getItem("token");

    if (storedUser && storedToken) {
      setUser(JSON.parse(storedUser));
      setToken(storedToken);
    }
    setLoading(false);
  }, []);

  // Fetch user profile with memoized function
  const fetchUserProfile = useCallback(
    async (userID) => {
      if (!token || !userID) return;

      try {
        setLoading(true);
        const response = await axios.get(
          `${config.apiBaseUrl}/api/auth/user/${userID}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );

        const userData = {
          ...response.data,
          userID: response.data.userID || userID,
          email: response.data.email,
          firstName: response.data.firstName,
          lastName: response.data.lastName,
          role: response.data.role || "user",
        };

        setUser(userData);
        setFavourites(response.data.Favourites || []);
        localStorage.setItem("user", JSON.stringify(userData));
      } catch (error) {
        console.error("Error fetching user profile:", error);
        logout(); // Logout on error to prevent stale data
      } finally {
        setLoading(false);
      }
    },
    [token]
  );

  // Fetch favourites when user or token changes
  useEffect(() => {
    if (user?.userID && token) {
      fetchUserProfile(user.userID);
    }
  }, [user?.userID, token, fetchUserProfile]);

  // Login function with full user data
  const login = useCallback(
    async (credentials, redirectPath = "/") => {
      try {
        setLoading(true);
        const response = await axios.post(
          `${config.apiBaseUrl}/api/auth/login`,
          credentials
        );
        const { token, userID, email, name, role } = response.data;

        const userData = { userID, email, firstName: name, role };
        setUser(userData);
        setToken(token);
        localStorage.setItem("user", JSON.stringify(userData));
        localStorage.setItem("token", token);

        // Fetch full profile after login
        await fetchUserProfile(userID);
        return redirectPath;
      } catch (error) {
        console.error("[AuthContext] Login failed:", error);
        throw error;
      } finally {
        setLoading(false);
      }
    },
    [fetchUserProfile]
  );

  // Logout function
  const logout = useCallback(() => {
    setUser(null);
    setToken(null);
    setFavourites([]);
    localStorage.removeItem("user");
    localStorage.removeItem("token");
    window.location.href = "/";
  }, []);

  // Update user details
  const updateUser = useCallback(
    async (updatedData) => {
      if (!user?.userID || !token) return;

      try {
        setLoading(true);
        const response = await axios.put(
          `${config.apiBaseUrl}/api/auth/user/${user.userID}/update`,
          updatedData,
          { headers: { Authorization: `Bearer ${token}` } }
        );

        const newUserData = { ...user, ...response.data };
        setUser(newUserData);
        localStorage.setItem("user", JSON.stringify(newUserData));
      } catch (error) {
        console.error("Error updating user:", error);
      } finally {
        setLoading(false);
      }
    },
    [user, token]
  );

  const value = {
    user,
    token,
    favourites,
    loading,
    login,
    logout,
    fetchUserProfile,
    updateUser,
    setFavourites,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
